import React, { useState, useEffect, useContext } from 'react';
import UserProfileContext from '../../context/userprofilecontext.js';
import { useForm, useFieldArray, Controller, get } from 'react-hook-form';
import { AssetsLiabilitiesTable, TotalsTable, getTableHeaders } from './table-asset-liability.js';
import { useGetAccountBalancesByCategory } from "../../api/accounts.js"
import MBSDisplayOptionsForm from './MBSDisplayOptionsForm.js';
import { useGetDRFAIncomeExpense, useUpdateDRFAIncomeExpense } from '../../api/drfa_income_expense.js';
import { useGetCaseDetails } from '../../api/casedetails';
import { formatDate, formatCurrency, getAge, getClientAge, getClientName, getSpouseAge, getSpouseName } from '../../utils/formatters.js';
import usePrintDRFAPDF from './PDFPrintDRFA.js';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';

// Material UI imports - only for the Children table in Personal Info
// (no design-system Table primitive yet; retained with tokenized sx styling)
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

// Design-system primitives
import { PageHeader, SectionTitle, Card, Badge } from '../../design-system';

// Loading skeleton (extracted)
import LoadingSkeleton from './drfa-sheet-skeleton.js';

import styles from './drfa-sheet.module.css';

// ---------- Simple icon components ----------
const Printer = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6,9 6,2 18,2 18,9"></polyline>
    <path d="M6,18H4a2,2,0,0,1-2-2V11a2,2,0,0,1,2,2H20a2,2,0,0,1,2,2v5a2,2,0,0,1-2,2H18"></path>
    <rect x="6" y="14" width="12" height="8"></rect>
  </svg>
);

const Plus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const Trash2 = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3,6 5,6 21,6"></polyline>
    <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const User = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20,21V19a4,4,0,0,0-4-4H8a4,4,0,0,0-4,4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const DollarSign = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17,5H9.5a3.5,3.5,0,0,0,0,7h5a3.5,3.5,0,0,1,0,7H6"></path>
  </svg>
);

const Home = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3,9L12,2L21,9V20a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2Z"></path>
    <polyline points="9,22 9,12 15,12 15,22"></polyline>
  </svg>
);

const BarChart3 = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="20" x2="12" y2="10"></line>
    <line x1="18" y1="20" x2="18" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="16"></line>
  </svg>
);

const AlertTriangle = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29,3.86L1.82,18a2,2,0,0,0,1.71,3H20.47a2,2,0,0,0,1.71-3L13.71,3.86A2,2,0,0,0,10.29,3.86Z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
);

// Minimum threshold for displaying subcategories by default
const MIN_AMOUNT_THRESHOLD = 10;

const calculateSectionSourceTotals = (section) => {
  const accounts = section.accounts || [];
  let petitionerTotal = 0;
  let respondentTotal = 0;
  let otherTotal = 0;
  let maritalTotal = 0;
  let petitionerSettlementTotal = 0;
  let respondentSettlementTotal = 0;

  accounts.forEach(account => {
    const sourceValues = calculateSourceValues(account);
    petitionerTotal += sourceValues.petitionerValue;
    respondentTotal += sourceValues.respondentValue;
    otherTotal += sourceValues.otherValue;
    maritalTotal += sourceValues.maritalValue;
    petitionerSettlementTotal += sourceValues.petitionerSettlementValue;
    respondentSettlementTotal += sourceValues.respondentSettlementValue;

    // Add associated liabilities
    if (account.associated_liabilities) {
      account.associated_liabilities.forEach(liability => {
        const liabilityValue = liability.latest_balance.end_balance || 0;
        petitionerTotal += liabilityValue * sourceValues.petitionerSource;
        respondentTotal += liabilityValue * sourceValues.respondentSource;
        otherTotal += liabilityValue * sourceValues.otherSource;
        const incrementalMaritalValue = liabilityValue * sourceValues.maritalSource;
        maritalTotal += incrementalMaritalValue;
        const incrementalPetitionerSettlement = incrementalMaritalValue * sourceValues.petitionerSettlement;
        petitionerSettlementTotal += incrementalPetitionerSettlement;
        const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
        respondentSettlementTotal += incrementalRespondentSettlement;
      });
    }
  });

  return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
};

const calculateAccountNetValue = (account) => {
  let netValue = account.latest_balance.end_balance || 0;
  if (account.associated_liabilities) {
    netValue += account.associated_liabilities.reduce((sum, liab) => sum + (liab.latest_balance.end_balance || 0), 0);
  }
  return netValue;
};

const calculateSectionTotal = (section) => {
  const accounts = section.accounts || [];
  return accounts.reduce((sum, account) => {
    return sum + calculateAccountNetValue(account);
  }, 0);
};

const sumSignedNetValues = (sections) => {
  return Object.values(sections || {}).reduce((sum, section) => sum + calculateSectionTotal(section), 0);
};

export const calculateAssetTotal = (assetLiabilityData) => {
  return sumSignedNetValues(assetLiabilityData?.assets);
};

// Liabilities are stored negative and an overpaid card carries a positive (credit) balance,
// so sum the signed values and flip the sign once. Taking abs() per account would count a
// credit as debt. This matches the Total Liabilities table row and the MBS.
export const calculateLiabilityTotal = (assetLiabilityData) => {
  // `|| 0` keeps an empty section from rendering as -0.
  return -sumSignedNetValues(assetLiabilityData?.liabilities) || 0;
};

const calculateSourceValues = (account) => {
  const end_balance = account.latest_balance.end_balance || 0;
  const petitionerSource = account._petitioner_owning_pct || 0;
  const petitionerValue = account.latest_balance._petitioner_owning_value ?? end_balance * petitionerSource;
  const respondentSource = account._respondent_owning_pct || 0;
  const respondentValue = account.latest_balance._respondent_owning_value ?? end_balance * respondentSource;
  const otherSource = account._stated_other_pct || 0;
  const otherValue = end_balance * otherSource;
  const maritalSource = account._stated_marital_pct ?? (1 - petitionerSource - respondentSource - otherSource);
  const petitionerSettlement = account._petitioner_settlement_pct || 0;
  const maritalValue = end_balance * maritalSource;
  const petitionerSettlementValue = account.latest_balance._petitioner_settlement_value ?? maritalValue * petitionerSettlement;
  const respondentSettlementValue = maritalValue - petitionerSettlementValue;

  return {
    petitionerValue: petitionerValue,
    respondentValue: respondentValue,
    otherValue,
    maritalValue,
    petitionerSource,
    respondentSource,
    otherSource,
    maritalSource,
    petitionerSettlement,
    respondentSettlement: 1 - petitionerSettlement,
    petitionerSettlementValue,
    respondentSettlementValue
  };
};

// Calculate equity source values for accounts with associated liabilities
const calculateEquitySourceValues = (account) => {
  const accountSourceValues = calculateSourceValues(account);
  let totalPetitionerValue = accountSourceValues.petitionerValue;
  let totalRespondentValue = accountSourceValues.respondentValue;
  let totalOtherValue = accountSourceValues.otherValue;
  let totalMaritalValue = accountSourceValues.maritalValue;
  let totalPetitionerSettlementValue = accountSourceValues.petitionerSettlementValue;
  let totalRespondentSettlementValue = accountSourceValues.respondentSettlementValue;

  if (account.associated_liabilities) {
    account.associated_liabilities.forEach(liability => {
      const liabilityValue = liability.latest_balance.end_balance || 0;
      totalPetitionerValue += liabilityValue * accountSourceValues.petitionerSource;
      totalRespondentValue += liabilityValue * accountSourceValues.respondentSource;
      totalOtherValue += liabilityValue * accountSourceValues.otherSource;
      const incrementalMaritalValue = liabilityValue * accountSourceValues.maritalSource;
      totalMaritalValue += incrementalMaritalValue;
      const incrementalPetitionerSettlement = incrementalMaritalValue * accountSourceValues.petitionerSettlement;
      totalPetitionerSettlementValue += incrementalPetitionerSettlement;
      const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
      totalRespondentSettlementValue += incrementalRespondentSettlement;
    });
  }

  return {
    petitionerValue: totalPetitionerValue,
    respondentValue: totalRespondentValue,
    otherValue: totalOtherValue,
    maritalValue: totalMaritalValue,
    petitionerSettlementValue: totalPetitionerSettlementValue,
    respondentSettlementValue: totalRespondentSettlementValue
  };
};

// Function to generate line numbers for all items
  const generateLineNumbers = (data) => {
    let lineNumber = 1;
    const lineNumbers = {};

    // Process assets first
    Object.entries(data.assets).forEach(([sectionName, section]) => {
      const accounts = section.accounts || [];
      accounts.forEach((account, index) => {
        // Use section name + account index + id to create unique key
        const accountKey = `assets-${sectionName}-${index}-${account.id}`;
        lineNumbers[accountKey] = lineNumber++;

        // Only add liability and equity lines if there are actually associated liabilities
        if (account.associated_liabilities && account.associated_liabilities.length > 0) {
          account.associated_liabilities.forEach(liability => {
            lineNumbers[`${accountKey}-liability-${liability.id}`] = lineNumber++;
          });
          // Add line number for net equity
          lineNumbers[`${accountKey}-equity`] = lineNumber++;
        }
      });
      // Add line number for section total
      lineNumbers[`assets-${sectionName}-total`] = lineNumber++;
    });

    // Add line number for total assets
    lineNumbers['total-assets'] = lineNumber++;

    // Then process liabilities
    Object.entries(data.liabilities || {}).forEach(([sectionName, section]) => {
      const accounts = section.accounts || [];
      accounts.forEach((account, index) => {
        // Use section name + account index + id to create unique key
        const accountKey = `liabilities-${sectionName}-${index}-${account.id}`;
        lineNumbers[accountKey] = lineNumber++;

        // Only add liability and equity lines if there are actually associated liabilities
        if (account.associated_liabilities && account.associated_liabilities.length > 0) {
          account.associated_liabilities.forEach(liability => {
            lineNumbers[`${accountKey}-liability-${liability.id}`] = lineNumber++;
          });
          // Add line number for net equity
          lineNumbers[`${accountKey}-equity`] = lineNumber++;
        }
      });
      // Add line number for section total
      lineNumbers[`liabilities-${sectionName}-total`] = lineNumber++;
    });

    // Add line number for total liabilities
    lineNumbers['total-liabilities'] = lineNumber++;

    // Add line number for net worth
    lineNumbers['net-worth'] = lineNumber++;

    console.log('Generated line numbers:', lineNumbers); // Debug log
    return lineNumbers;
  };

// ---------- Shared tokenized sx snippets for the MUI Children table ----------
// Using var(--...) inside sx lets us keep tokens without introducing a new
// design-system Table primitive for a one-off use case.
const childrenTableSx = { border: '1px solid var(--color-gray-200)' };
const childrenHeadRowSx = { bgcolor: 'var(--surface-secondary)' };
const childrenHeadCellSx = {
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--text-primary)',
  borderRight: '1px solid var(--color-gray-200)',
  py: 'var(--space-3)',
};
const childrenHeadCellLastSx = {
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--text-primary)',
  py: 'var(--space-3)',
};
const childrenBodyRowSx = {
  '&:nth-of-type(even)': { bgcolor: 'var(--surface-secondary)' },
  borderBottom: '1px solid var(--color-gray-200)',
};
const childrenBodyCellSx = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm)',
  color: 'var(--text-primary)',
  borderRight: '1px solid var(--color-gray-200)',
  py: 'var(--space-3)',
  fontWeight: 'var(--font-medium)',
};
const childrenBodyCellLastSx = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm)',
  color: 'var(--text-primary)',
  py: 'var(--space-3)',
};

const DRFA = () => {
  const navigate = useNavigate();
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext?.userProfile || {};
  // const currentCase = userProfile.currentCase || {};
  // const currentOrg = userProfile.currentOrg || {};

  console.log("User Profile in DRFA:", userProfile);
  console.log("isSuperAdmin:", userProfile.isSuperAdmin);
  console.log("isOrgAdmin:", userProfile.isOrgAdmin());
  console.log("isCaseAdmin:", userProfile.isCaseAdmin());
  console.log("isCaseEditor:", userProfile.isCaseEditor());
  console.log("isCaseContributor:", userProfile.isCaseContributor());
  console.log("isCaseViewer:", userProfile.isCaseViewer());

  let { data: assetLiabilityData, error: assetLiabilityError, isLoading: assetLiabilityIsLoading } = useGetAccountBalancesByCategory();
  const lineNumbers = generateLineNumbers(assetLiabilityData || { assets: {}, liabilities: {} });
  let { data: drfaIncomeExpenseData, error: drfaIncomeExpenseError, isLoading: drfaIncomeExpenseIsLoading } = useGetDRFAIncomeExpense();
  const { data: caseDetails, error: caseDetailsError, isLoading: caseDetailsIsLoading } = useGetCaseDetails()
  const updateDRFAIncomeExpense = useUpdateDRFAIncomeExpense();

  const [activeTab, setActiveTab] = useState(0);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [showZeroSubcategories, setShowZeroSubcategories] = useState({});
  const [reportForClient, setReportForClient] = useState(true);

  // NEW: Support payment state variables
  const [spousalSupportNeeded, setSpousalSupportNeeded] = useState(0);
  const [childSupportIndicated, setChildSupportIndicated] = useState(0);

  // Display options state
  const [displayOptions, setDisplayOptions] = useState({
    isDraft: true,
    showNotes: true,
    showSourceColumns: true,
    showSettlementColumns: true,
    showSupportFields: false
  });

  // UPDATED: Pass spousal and child support to the PDF print hook
  const handlePrint = usePrintDRFAPDF(
    assetLiabilityData,
    drfaIncomeExpenseData,
    displayOptions,
    reportForClient,
    caseDetails?.monthlySpousalSupport,
    caseDetails?.monthlyChildSupport
  );

  // React Hook Form setup for main form
  const {
    register,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      yourName: 'John Michael Smith',
      yourAge: '48',
      spouseName: 'Maria Elena Rodriguez',
      spouseAge: '47',
      dateOfMarriage: '2000-09-23',
      dateOfSeparation: '2023-09-21',
      caseNumber: '2024-CV-12345',
      county: 'Forsyth County',
      state: 'Georgia',
      payPeriod: 'Weekly, Monthly, etc.',
      taxExemptions: '2',
      childrenForSupport: [{ name: '', dateOfBirth: '', residesWith: '' }],
      grossMonthlyIncome: 0,
      netMonthlyIncome: 0,
      averageMonthlyExpenses: 0,
      monthlyPaymentsToCreditors: 0,
      totalMonthlyExpenses: 0,
      income: {
        salaryWages: '',
        commissionsFeesTips: '',
        selfEmploymentIncome: '',
        rentalIncome: '',
        bonuses: '',
        overtimePayments: '',
        pensionRetirement: '',
        interestDividends: '',
        socialSecurityBenefits: '',
        otherIncome: ''
      },
      assets: {
        cash: { value: '', husbandSeparate: '', wifeSeparate: '', basis: '' },
        vehicles: [{ description: 'Vehicle 1', value: '', husbandSeparate: '', wifeSeparate: '', basis: '' }],
        otherAssets: [{ description: '', value: '', husbandSeparate: '', wifeSeparate: '', basis: '' }]
      },
      expenses: {
        mortgageRent: '',
        propertyTaxes: '',
        homeownersInsurance: '',
        electricity: '',
        water: '',
        garbageSewer: '',
        residentialPhone: '',
        cellularPhone: '',
        gas: '',
        repairsMaintenance: '',
        miscHouseholdGrocery: '',
        mealsOutsideHome: ''
      },
      creditorPayments: [{ creditor: '', balanceDue: '', monthlyPayment: '', joint: false, husband: false, wife: false }]
    }
  });

  const calculateCategoryTotal = (category) => {
    if (!assetLiabilityData || !assetLiabilityData[category]) return 0;
    return Object.entries(assetLiabilityData[category]).reduce((sum, [_, section]) => {
      return sum + calculateSectionTotal(section);
    }, 0);
  };

  const calculateCategorySourceTotals = (category) => {
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let otherTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    if (!assetLiabilityData || !assetLiabilityData[category]) {
      return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
    }

    Object.entries(assetLiabilityData[category]).forEach(([_, section]) => {
      const sourceTotals = calculateSectionSourceTotals(section);
      petitionerTotal += sourceTotals.petitionerTotal;
      respondentTotal += sourceTotals.respondentTotal;
      otherTotal += sourceTotals.otherTotal;
      maritalTotal += sourceTotals.maritalTotal;
      petitionerSettlementTotal += sourceTotals.petitionerSettlementTotal;
      respondentSettlementTotal += sourceTotals.respondentSettlementTotal;
    });

    return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
  };

  // Separate React Hook Form setup for DRFA stated amounts
  const drfaForm = useForm({
    defaultValues: {}
  });

  // Field arrays for dynamic sections
  const {
    fields: childrenFields,
    append: appendChild,
    remove: removeChild
  } = useFieldArray({
    control,
    name: "childrenForSupport"
  });

  const formData = watch();
  const incomeValues = watch('income');
  const expenseValues = watch('expenses');

  useEffect(() => {
    // Initialize DRFA form values when data loads
    if (drfaIncomeExpenseData) {
      const defaultValues = {};
      drfaIncomeExpenseData.forEach(item => {
        defaultValues[`stated_amount_${item.drfa_category_name}`] = item.stated_amount || '';
      });
      drfaForm.reset(defaultValues);
    }
  }, [drfaIncomeExpenseData, drfaForm]);

  useEffect(() => {
    // Calculate totals whenever income, expenses, or creditor payments change
    const grossIncome = Object.values(incomeValues || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const totalExpenses = Object.values(expenseValues || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const totalMonthlyExpenses = totalExpenses;

    // Update calculated fields
    setValue('grossMonthlyIncome', grossIncome);
    setValue('averageMonthlyExpenses', totalExpenses);
    setValue('totalMonthlyExpenses', totalMonthlyExpenses);
  }, [incomeValues, expenseValues, setValue]);

  // LOADING STATE CHECK - AFTER ALL HOOKS
  const isLoading = assetLiabilityIsLoading || drfaIncomeExpenseIsLoading || caseDetailsIsLoading;

  // SHOW SKELETON LOADING
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const handleDRFAAmountChange = async (drfaItemType, drfaCategoryName, newStatedAmount) => {
    console.log('Updating DRFA amount:', drfaItemType, drfaCategoryName, newStatedAmount);
    if (!newStatedAmount || newStatedAmount === '' || isNaN(newStatedAmount)) {
      newStatedAmount = null;
    } else {
      newStatedAmount = parseFloat(newStatedAmount);
    }

    // Create optimistic update
    const optimisticData = drfaIncomeExpenseData.map(item =>
      item.drfa_category_name === drfaCategoryName
        ? { ...item, stated_amount: newStatedAmount }
        : item
    );

    try {
      updateDRFAIncomeExpense(drfaItemType, drfaCategoryName, newStatedAmount, optimisticData);
    } catch (error) {
      console.error('Error updating DRFA amount:', error);
    }
  };

  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleZeroSubcategories = (categoryName) => {
    setShowZeroSubcategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const renderIncomeExpenseTab = (type) => {
    if (!drfaIncomeExpenseData) return <div>Loading...</div>;

    // Filter data based on type
    const filteredData = drfaIncomeExpenseData.filter(item => {
      if (type === 'Income') {
        return item.type === 'Income';
      } else {
        return item.type !== 'Income';
      }
    });

    // Group by category
    const groupedData = filteredData.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});

    const calculateCategoryTotal = (items, field) => {
      return items.reduce((sum, item) => {
        const value = item[field];
        return sum + (parseFloat(value) || 0);
      }, 0);
    };

    const totalForType = filteredData.reduce((sum, item) => {
      const statedAmount = parseFloat(item.stated_amount) || 0;
      return sum + (type === 'Income' ? statedAmount : Math.abs(statedAmount));
    }, 0);

    const summaryClass = type === 'Income' ? styles.tabSummarySuccess : styles.tabSummaryError;

    return (
      <div>
        <div className={styles.tabHeader}>
          <SectionTitle>{type} Information</SectionTitle>
        </div>

        {Object.entries(groupedData).map(([categoryName, items]) => {
          const categoryMonthlyTotal = calculateCategoryTotal(items, 'monthly_average_amount');
          const categoryStatedTotal = calculateCategoryTotal(items, 'stated_amount');

          // Auto-collapse categories with zero totals
          const isCollapsed = collapsedCategories[categoryName] ?? (categoryMonthlyTotal === 0);
          const showZeros = showZeroSubcategories[categoryName] ?? false;

          // Filter items based on whether to show zero subcategories
          const visibleItems = showZeros ? items : items.filter(item =>
            item.kind === 'stated' ||
            (item.monthly_average_amount && Math.abs(parseFloat(item.monthly_average_amount)) >= MIN_AMOUNT_THRESHOLD) ||
            (item.stated_amount != null && item.stated_amount !== '')
          );
          const zeroItems = items.filter(item =>
            item.kind !== 'stated' &&
            (!item.monthly_average_amount || Math.abs(parseFloat(item.monthly_average_amount)) < MIN_AMOUNT_THRESHOLD) &&
            (item.stated_amount == null || item.stated_amount === '')
          );

          return (
            <div key={categoryName} className={styles.category}>
              {/* Category Header */}
              <button
                type="button"
                className={`${styles.categoryHeader} ${!isCollapsed ? styles.categoryHeaderOpen : ''}`}
                onClick={() => toggleCategory(categoryName)}
              >
                <div className={styles.categoryHeaderLeft}>
                  {isCollapsed ? <ChevronRight /> : <ChevronDown />}
                  <h3 className={styles.categoryTitle}>
                    {categoryName}
                  </h3>
                  {categoryMonthlyTotal === 0 && (
                    <span className={styles.noActivityBadge}>
                      No Activity
                    </span>
                  )}
                </div>
                <div className={styles.categoryTotals}>
                  <span>Monthly Avg: {formatCurrency(categoryMonthlyTotal)}</span>
                  <span>Stated: {formatCurrency(categoryStatedTotal)}</span>
                </div>
              </button>

              {/* Category Content */}
              {!isCollapsed && (
                <div className={styles.categoryBody}>
                  {/* Toggle for zero subcategories */}
                  {zeroItems.length > 0 && (
                    <div className={styles.subcategoryToggleWrap}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleZeroSubcategories(categoryName);
                        }}
                        className={styles.subcategoryToggle}
                      >
                        {showZeros ? <ChevronDown /> : <ChevronRight />}
                        {showZeros ? 'Hide' : 'Show'} {zeroItems.length} subcategories with minimal activity (under ${MIN_AMOUNT_THRESHOLD})
                      </button>
                    </div>
                  )}

                  <div className={styles.tableScroll}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th className={styles.dataTableTh}>
                            Subcategory
                          </th>
                          <th className={`${styles.dataTableTh} ${styles.dataTableThRight}`}>
                            Monthly Average
                          </th>
                          <th className={`${styles.dataTableTh} ${styles.dataTableThRight}`}>
                            Stated Amount
                          </th>
                          <th className={`${styles.dataTableTh} ${styles.dataTableThCenter}`}>
                            Transactions
                          </th>
                          <th className={`${styles.dataTableTh} ${styles.dataTableThCenter}`}>
                            Date Range
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleItems.map((item, index) => (
                          <tr key={index} className={styles.dataTableRow}>
                            <td className={styles.dataTableCell}>
                              {item.subcategory}
                            </td>
                            <td className={`${styles.dataTableCell} ${styles.dataTableCellRight} ${styles.dataTableCellMono}`}>
                              {formatCurrency(item.monthly_average_amount || 0)}
                            </td>

                            {
                              item.kind === 'measured' &&
                              <td className={`${styles.dataTableCell} ${styles.dataTableCellRight}`}>
                                <span className={styles.dataTableMuted}>Measured</span>
                              </td>
                            }
                            {
                              item.kind !== 'measured' && !userProfile.isCaseContributor() &&
                              <td className={`${styles.dataTableCell} ${styles.dataTableCellRight} ${styles.dataTableCellMono}`}>
                                {formatCurrency(item.stated_amount || 0)}
                              </td>
                            }
                            {/* HERE */
                              item.kind !== 'measured' && userProfile.isCaseContributor() &&
                              <td className={styles.dataTableCell}>
                                <div className={styles.dollarInputWrap}>
                                  <span className={styles.dollarPrefix}>$</span>
                                  <Controller
                                    name={`stated_amount_${item.drfa_category_name}`}
                                    control={drfaForm.control}
                                    defaultValue={item.stated_amount || ''}
                                    render={({ field, fieldState }) => (
                                      <input
                                        {...field}
                                        type="number"
                                        className={styles.dollarInput}
                                        onChange={(e) => {
                                          field.onChange(e.target.value);
                                        }}
                                        onBlur={(e) => {
                                          field.onBlur();
                                          handleDRFAAmountChange(item.type, item.drfa_category_name, field.value);
                                        }}
                                        placeholder="0.00"
                                        step="0.01"
                                      />
                                    )}
                                  />
                                </div>
                              </td>
                            }

                            <td className={`${styles.dataTableCell} ${styles.dataTableCellCenter}`}>
                              {(item.transaction_count || 0) > 0 && item.kind === 'measured' && item.cross_ledger ? (
                                <button
                                  type="button"
                                  className={styles.transactionCountLink}
                                  onClick={() => {
                                    // Drill into "Business activity in personal
                                    // accounts": the server resolves the same rows
                                    // the line counts (BUSINESS-chart income and
                                    // expenses on personal accounts in the window).
                                    const params = new URLSearchParams();
                                    params.set('drfa_cross_ledger', item.cross_ledger);
                                    navigate(`/report_sankey_analysis/CATEGORY?${params.toString()}`);
                                  }}
                                  aria-label={`View ${item.transaction_count} transactions for ${item.subcategory}`}
                                >
                                  {item.transaction_count}
                                </button>
                              ) : (item.transaction_count || 0) > 0 && item.kind === 'measured' && item.business_account_id ? (
                                <button
                                  type="button"
                                  className={styles.transactionCountLink}
                                  onClick={() => {
                                    // Drill into the business account's measured
                                    // self-employment line: the server resolves the
                                    // same rows the line counts (BUSINESS-chart income
                                    // and expenses in the report window).
                                    const params = new URLSearchParams();
                                    params.set('drfa_business_account', item.business_account_id);
                                    navigate(`/report_sankey_analysis/CATEGORY?${params.toString()}`);
                                  }}
                                  aria-label={`View ${item.transaction_count} transactions for ${item.subcategory}`}
                                >
                                  {item.transaction_count}
                                </button>
                              ) : (item.transaction_count || 0) > 0 && item.drfa_category_name ? (
                                <button
                                  type="button"
                                  className={styles.transactionCountLink}
                                  onClick={() => {
                                    // Drill down into Transaction Analysis (Sankey page).
                                    // We pass the DRFA category name (not Livisibly
                                    // category/subcategory — those are different
                                    // hierarchies). The receiving page resolves it to
                                    // an exact list of transaction IDs server-side via
                                    // the MAPS_TO traversal, so the user sees the same
                                    // transactions that built the DRFA's monthly average.
                                    const params = new URLSearchParams();
                                    params.set('drfa_category', item.drfa_category_name);
                                    navigate(`/report_sankey_analysis/CATEGORY?${params.toString()}`);
                                  }}
                                  aria-label={`View ${item.transaction_count} transactions for ${item.drfa_category_name}`}
                                >
                                  {item.transaction_count}
                                </button>
                              ) : (
                                item.transaction_count || 0
                              )}
                            </td>
                            <td className={`${styles.dataTableCell} ${styles.dataTableCellCenter}`}>
                              {item.earliest_transaction && item.latest_transaction ? (
                                <div>
                                  <div>{formatDate(item.earliest_transaction)}</div>
                                  <div className={styles.dataTableMuted}>
                                    to {formatDate(item.latest_transaction)}
                                  </div>
                                </div>
                              ) : (
                                <span className={styles.dataTableEmpty}>No transactions</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Summary */}
        <div className={`${styles.tabSummary} ${summaryClass}`}>
          <h4 className={styles.tabSummaryTitle}>
            Total {type}: {formatCurrency(totalForType)}
          </h4>
        </div>
      </div>
    );
  };

  // Calculate income total from DRFA data
  const calculateIncomeTotal = () => {
    if (!drfaIncomeExpenseData) return 0;
    return drfaIncomeExpenseData
      .filter(item => item.type === 'Income')
      .reduce((sum, item) => {
        const statedAmount = parseFloat(item.stated_amount) || 0;
        return sum + statedAmount;
      }, 0);
  };

  // Calculate expense total from DRFA data
  const calculateExpenseTotal = () => {
    if (!drfaIncomeExpenseData) return 0;
    return drfaIncomeExpenseData
      .filter(item => item.type !== 'Income')
      .reduce((sum, item) => {
        const statedAmount = parseFloat(item.stated_amount) || 0;
        return sum + Math.abs(statedAmount);
      }, 0);
  };

  // Calculate derived values for display using API data
  const currentGrossIncome = calculateIncomeTotal();
  const currentTotalExpenses = calculateExpenseTotal();
  const currentTotalAssets = calculateAssetTotal(assetLiabilityData);
  const currentTotalLiabilities = calculateLiabilityTotal(assetLiabilityData);
  const monthlyBalance = currentGrossIncome - currentTotalExpenses;
  const netWorth = currentTotalAssets - currentTotalLiabilities;

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'income', label: 'Income', icon: DollarSign },
    { id: 'assets', label: 'Assets', icon: BarChart3 },
    { id: 'expenses', label: 'Expenses', icon: Home },
    { id: 'debts', label: 'Debts', icon: BarChart3 }
  ];

  // Subtitle for the PageHeader — the court and state line
  const headerSubtitle = `${caseDetails?.courtName || 'COURT NAME'} \u2014 STATE OF ${caseDetails?.jurisdictionState || 'GEORGIA'}`;

  // Right-aligned actions on the header
  const headerActions = (
    <div className={styles.headerActions}>
      {caseDetails?.caseNumber && (
        <Badge variant="info" size="md">Case #: {caseDetails.caseNumber}</Badge>
      )}
      <div className={styles.reportToggle}>
        <span className={styles.reportToggleLabel}>Report for:</span>
        <span
          className={`${styles.reportToggleOption} ${!reportForClient ? styles.reportToggleOptionActive : ''}`}
        >
          Spouse
        </span>
        <button
          type="button"
          onClick={() => setReportForClient(!reportForClient)}
          className={`${styles.reportToggleSwitch} ${reportForClient ? styles.reportToggleSwitchOn : ''}`}
          aria-label={`Report is for ${reportForClient ? 'Client' : 'Spouse'}. Click to switch.`}
        >
          <span
            className={`${styles.reportToggleKnob} ${reportForClient ? styles.reportToggleKnobOn : ''}`}
          />
        </button>
        <span
          className={`${styles.reportToggleOption} ${reportForClient ? styles.reportToggleOptionActive : ''}`}
        >
          Client
        </span>
      </div>
      <button type="button" onClick={handlePrint} className={styles.printButton}>
        <Printer />
        Print PDF
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Page header */}
      <PageHeader
        title="Domestic Relations Financial Affidavit"
        subtitle={headerSubtitle}
        actions={headerActions}
      />

      {/* Case info strip */}
      <section className={styles.sectionGap}>
        <Card padding="md">
          <div className={styles.caseInfoStrip}>
            <div className={styles.caseInfoItem}>
              <span className={styles.caseInfoLabel}>Petitioner</span>
              <span className={styles.caseInfoValue}>
                {caseDetails?.petitioner?.fullName || 'PETITIONER NAME'}
              </span>
            </div>
            <div className={styles.caseInfoItem}>
              <span className={styles.caseInfoLabel}>Respondent</span>
              <span className={styles.caseInfoValue}>
                {caseDetails?.respondent?.fullName || 'RESPONDENT NAME'}
              </span>
            </div>
            <div className={styles.caseInfoItem}>
              <span className={styles.caseInfoLabel}>Date of Marriage</span>
              <span className={styles.caseInfoValue}>
                {caseDetails?.marriageDate ? formatDate(caseDetails?.marriageDate) : '\u2014'}
              </span>
            </div>
          </div>
        </Card>
      </section>

      {/* Main form section */}
      <section className={styles.sectionGap}>
        <Card padding="none">
          <MBSDisplayOptionsForm
            displayOptions={displayOptions}
            setDisplayOptions={setDisplayOptions}
          />
          {/* Tab Navigation */}
          <div className={styles.tabs} role="tablist">
            {tabs.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = activeTab === index;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(index)}
                  className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                >
                  <Icon />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className={styles.tabPane}>
            {activeTab === 0 && (
              <div>
                <div className={styles.tabHeader}>
                  <SectionTitle
                    actions={
                      userProfile.canAccess('/case_maintenance_sheet') ? (
                        <button
                          type="button"
                          className={styles.editButton}
                          onClick={() => navigate('/case_maintenance_sheet')}
                        >
                          <EditIcon fontSize="small" />
                          Edit
                        </button>
                      ) : null
                    }
                  >
                    Personal Information
                  </SectionTitle>
                </div>

                {/* Parties Information */}
                <div className={styles.personalSection}>
                  <Card padding="md">
                    <h3 className={styles.personalCardTitle}>Parties to the Case</h3>
                    <div className={styles.partiesGrid}>
                      <div className={styles.partyBox}>
                        <p className={styles.partyLabel}>
                          {reportForClient ? 'Client' : 'Spouse'}
                        </p>
                        <p className={styles.partyName}>
                          {reportForClient ? getClientName(caseDetails) : getSpouseName(caseDetails)}
                        </p>
                        <p className={styles.partyAge}>
                          Age: {reportForClient ? getClientAge(caseDetails) : getSpouseAge(caseDetails)} years
                        </p>
                      </div>
                      <div className={styles.partyBox}>
                        <p className={styles.partyLabel}>
                          {reportForClient ? 'Spouse' : 'Client'}
                        </p>
                        <p className={styles.partyName}>
                          {reportForClient ? getSpouseName(caseDetails) : getClientName(caseDetails)}
                        </p>
                        <p className={styles.partyAge}>
                          Age: {reportForClient ? getSpouseAge(caseDetails) : getClientAge(caseDetails)} years
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Marriage Information */}
                <div className={styles.personalSection}>
                  <Card padding="md">
                    <h3 className={styles.personalCardTitle}>Marriage Information</h3>
                    <div className={styles.marriageGrid}>
                      <div>
                        <p className={styles.marriageItemLabel}>Date of Marriage</p>
                        <p className={styles.marriageItemValue}>
                          {caseDetails?.marriageDate ? formatDate(caseDetails?.marriageDate) : 'Not Available'}
                        </p>
                      </div>
                      <div>
                        <p className={styles.marriageItemLabel}>Date of Separation</p>
                        <p className={styles.marriageItemValue}>
                          {caseDetails?.separationDate ? formatDate(caseDetails?.separationDate) : 'Not Available'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Children Information */}
                <div className={styles.personalSection}>
                  <Card padding="md">
                    <h3 className={styles.personalCardTitle}>
                      Children Information ({caseDetails?.children?.length || 0} child{(caseDetails?.children?.length || 0) !== 1 ? 'ren' : ''})
                    </h3>

                    {caseDetails?.children?.length > 0 ? (
                      <TableContainer>
                        <Table sx={childrenTableSx}>
                          <TableHead>
                            <TableRow sx={childrenHeadRowSx}>
                              <TableCell sx={childrenHeadCellSx}>Child's Name</TableCell>
                              <TableCell sx={childrenHeadCellSx}>Date of Birth</TableCell>
                              <TableCell sx={childrenHeadCellSx}>Age</TableCell>
                              <TableCell sx={childrenHeadCellSx}>Resides With</TableCell>
                              <TableCell sx={childrenHeadCellLastSx}>Support Needed</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {caseDetails.children.map((child, index) => (
                              <TableRow key={child.id || index} sx={childrenBodyRowSx}>
                                <TableCell sx={childrenBodyCellSx}>
                                  {child.fullName || 'NOT PROVIDED'}
                                </TableCell>
                                <TableCell sx={childrenBodyCellSx}>
                                  {child.birthDate ? formatDate(child.birthDate) : 'Not specified'}
                                </TableCell>
                                <TableCell sx={childrenBodyCellSx}>
                                  {child.birthDate ? getAge(child.birthDate) : 'N/A'}
                                </TableCell>
                                <TableCell sx={childrenBodyCellSx}>
                                  {child.residesWith || 'Not specified'}
                                </TableCell>
                                <TableCell sx={childrenBodyCellLastSx}>
                                  <Badge variant={child.supportNeeded ? 'error' : 'success'} size="sm">
                                    {child.supportNeeded ? 'Yes' : 'No'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <p className={styles.childrenEmpty}>
                        No children information available
                      </p>
                    )}
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 1 && renderIncomeExpenseTab('Income')}

            {activeTab === 2 && (
              <div>
                <div className={styles.tabHeader}>
                  <SectionTitle
                    actions={
                      userProfile.canAccess('/manage_accounts') ? (
                        <button
                          type="button"
                          className={styles.editButton}
                          onClick={() => navigate('/manage_accounts')}
                        >
                          <EditIcon fontSize="small" />
                          Edit
                        </button>
                      ) : null
                    }
                  >
                    Assets &amp; Property
                  </SectionTitle>
                </div>

                <AssetsLiabilitiesTable
                  accountType="assets"
                  data={assetLiabilityData || {}}
                  displayOptions={displayOptions}
                  lineNumbers={lineNumbers}
                />
                {(() => {
                          const assetSourceTotals = calculateCategorySourceTotals('assets');
                          return (
                            <TotalsTable
                              displayOptions={displayOptions}
                              title="Total Assets"
                              lineNumber={lineNumbers['total-assets']}
                              value={calculateCategoryTotal('assets')}
                              petitionerTotal={assetSourceTotals.petitionerTotal}
                              respondentTotal={assetSourceTotals.respondentTotal}
                              otherTotal={assetSourceTotals.otherTotal}
                              maritalTotal={assetSourceTotals.maritalTotal}
                              petitionerSettlementTotal={assetSourceTotals.petitionerSettlementTotal}
                              respondentSettlementTotal={assetSourceTotals.respondentSettlementTotal}
                            />
                          );
                        })()}

                <div className={`${styles.tabSummary} ${styles.tabSummaryInfo}`}>
                  <h4 className={styles.tabSummaryTitle}>
                    Total Assets Value: {formatCurrency(currentTotalAssets)}
                  </h4>
                </div>
              </div>
            )}

            {activeTab === 3 && renderIncomeExpenseTab('Expense')}

            {activeTab === 4 && (
              <div>
                <div className={styles.tabHeader}>
                  <SectionTitle
                    actions={
                      userProfile.canAccess('/manage_accounts') ? (
                        <button
                          type="button"
                          className={styles.editButton}
                          onClick={() => navigate('/manage_accounts')}
                        >
                          <EditIcon fontSize="small" />
                          Edit
                        </button>
                      ) : null
                    }
                  >
                    Your Debts to Creditors
                  </SectionTitle>
                </div>

                <AssetsLiabilitiesTable
                  accountType="liabilities"
                  data={assetLiabilityData || {}}
                  displayOptions={displayOptions}
                  lineNumbers={lineNumbers}
                />
                {(() => {
                          const liabilitySourceTotals = calculateCategorySourceTotals('liabilities');
                          return (
                            <TotalsTable
                              displayOptions={displayOptions}
                              title="Total Liabilities"
                              lineNumber={lineNumbers['total-liabilities']}
                              value={calculateCategoryTotal('liabilities')}
                              petitionerTotal={liabilitySourceTotals.petitionerTotal}
                              respondentTotal={liabilitySourceTotals.respondentTotal}
                              otherTotal={liabilitySourceTotals.otherTotal}
                              maritalTotal={liabilitySourceTotals.maritalTotal}
                              petitionerSettlementTotal={liabilitySourceTotals.petitionerSettlementTotal}
                              respondentSettlementTotal={liabilitySourceTotals.respondentSettlementTotal}
                            />
                          );
                        })()}

                <div className={`${styles.tabSummary} ${styles.tabSummaryError}`}>
                  <h4 className={styles.tabSummaryTitle}>
                    Total Liabilities: {formatCurrency(currentTotalLiabilities)}
                  </h4>
                </div>
              </div>
            )}
          </div>
        </Card>
      </section>

      {/* Summary of Income & Needs */}
      <section className={styles.sectionGap}>
        <Card padding="md">
          <SectionTitle>Summary of Income &amp; Needs</SectionTitle>
          <table className={styles.summaryTable}>
            <tbody>
              <tr className={styles.summaryRow}>
                <td className={styles.summaryLabel}>
                  Gross monthly income (seen as "Total Income" below)
                </td>
                <td className={`${styles.summaryValue} ${styles.summaryValuePositive}`}>
                  {formatCurrency(currentGrossIncome)}
                </td>
              </tr>
              <tr className={styles.summaryRow}>
                <td className={styles.summaryLabel}>
                  Average monthly expenses (seen as "Total Expenses" below)
                </td>
                <td className={`${styles.summaryValue} ${styles.summaryValueNegative}`}>
                  {formatCurrency(currentTotalExpenses)}
                </td>
              </tr>
              <tr className={styles.summaryTotalRow}>
                <td className={styles.summaryLabel}>
                  Net monthly income (Gross monthly income minus average monthly expenses)
                </td>
                <td className={`${styles.summaryValue} ${monthlyBalance >= 0 ? styles.summaryValuePositive : styles.summaryValueNegative}`}>
                  {formatCurrency(monthlyBalance)}
                </td>
              </tr>
              {displayOptions.showSupportFields && (
                <>
                  <tr className={styles.summaryRow}>
                    <td className={styles.summaryLabel}>
                      Amount of Spousal Support Needed:
                    </td>
                    <td className={styles.summaryValue}>
                      {formatCurrency(caseDetails?.monthlySpousalSupport || 0)}
                    </td>
                  </tr>
                  <tr className={styles.summaryRow}>
                    <td className={styles.summaryLabel}>
                      Amount of Child Support Indicated by Child Support Guidelines:
                    </td>
                    <td className={styles.summaryValue}>
                      {formatCurrency(caseDetails?.monthlyChildSupport || 0)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </Card>
      </section>

      {/* Financial Summary */}
      <section className={styles.sectionGap}>
        <Card padding="lg">
          <SectionTitle>Financial Summary</SectionTitle>

          {/* Monthly Cash Flow */}
          <div>
            <h3 className={styles.finSubTitle}>Monthly Cash Flow</h3>
            <div className={styles.finGrid}>
              <div className={`${styles.finStat} ${styles.finStatSuccess}`}>
                <span className={`${styles.finStatIcon} ${styles.finStatIconSuccess}`}>
                  <DollarSign />
                </span>
                <p className={styles.finStatLabel}>Gross Income</p>
                <p className={`${styles.finStatValue} ${styles.finStatValueSuccess}`}>
                  {formatCurrency(currentGrossIncome)}
                </p>
              </div>

              <div className={`${styles.finStat} ${styles.finStatError}`}>
                <span className={`${styles.finStatIcon} ${styles.finStatIconError}`}>
                  <Home />
                </span>
                <p className={styles.finStatLabel}>Total Expenses</p>
                <p className={`${styles.finStatValue} ${styles.finStatValueError}`}>
                  {formatCurrency(currentTotalExpenses)}
                </p>
              </div>

              <div className={`${styles.finStat} ${monthlyBalance >= 0 ? styles.finStatSuccess : styles.finStatError}`}>
                <span className={`${styles.finStatIcon} ${monthlyBalance >= 0 ? styles.finStatIconSuccess : styles.finStatIconError}`}>
                  <DollarSign />
                </span>
                <p className={styles.finStatLabel}>Monthly Balance</p>
                <p className={`${styles.finStatValue} ${monthlyBalance >= 0 ? styles.finStatValueSuccess : styles.finStatValueError}`}>
                  {formatCurrency(monthlyBalance)}
                </p>
              </div>
            </div>
          </div>

          {/* Balance Sheet */}
          <div>
            <h3 className={styles.finSubTitle}>Balance Sheet</h3>
            <div className={styles.finGrid}>
              <div className={`${styles.finStat} ${styles.finStatInfo}`}>
                <span className={`${styles.finStatIcon} ${styles.finStatIconInfo}`}>
                  <BarChart3 />
                </span>
                <p className={styles.finStatLabel}>Total Assets</p>
                <p className={`${styles.finStatValue} ${styles.finStatValueInfo}`}>
                  {formatCurrency(currentTotalAssets)}
                </p>
              </div>

              <div className={`${styles.finStat} ${styles.finStatError}`}>
                <span className={`${styles.finStatIcon} ${styles.finStatIconError}`}>
                  <AlertTriangle />
                </span>
                <p className={styles.finStatLabel}>Total Liabilities</p>
                <p className={`${styles.finStatValue} ${styles.finStatValueError}`}>
                  {formatCurrency(currentTotalLiabilities)}
                </p>
              </div>

              <div className={`${styles.finStat} ${netWorth >= 0 ? styles.finStatSuccess : styles.finStatError}`}>
                <span className={`${styles.finStatIcon} ${netWorth >= 0 ? styles.finStatIconSuccess : styles.finStatIconError}`}>
                  {netWorth >= 0 ? <DollarSign /> : <AlertTriangle />}
                </span>
                <p className={styles.finStatLabel}>Net Worth</p>
                <p className={`${styles.finStatValue} ${netWorth >= 0 ? styles.finStatValueSuccess : styles.finStatValueError}`}>
                  {formatCurrency(netWorth)}
                </p>
                <p className={styles.finStatNote}>(Assets - Liabilities)</p>
              </div>
            </div>
          </div>

          {/* Warning Messages */}
          <div className={styles.warnings}>
            {monthlyBalance < 0 && (
              <div className={`${styles.warning} ${styles.warningError}`}>
                <p className={styles.warningText}>
                  <strong>Notice:</strong> Monthly expenses exceed income by {formatCurrency(Math.abs(monthlyBalance))}
                </p>
              </div>
            )}

            {netWorth < 0 && (
              <div className={`${styles.warning} ${styles.warningError}`}>
                <p className={styles.warningText}>
                  <strong>Notice:</strong> Total liabilities exceed total assets by {formatCurrency(Math.abs(netWorth))}
                </p>
              </div>
            )}

            {monthlyBalance >= 0 && netWorth >= 0 && (
              <div className={`${styles.warning} ${styles.warningSuccess}`}>
                <p className={styles.warningText}>
                  <strong>Positive Financial Position:</strong> Monthly surplus of {formatCurrency(monthlyBalance)} and net worth of {formatCurrency(netWorth)}
                </p>
              </div>
            )}
          </div>
        </Card>
      </section>
    </div>
  );
};

export default DRFA;
